import {
  DeviceMessage,
  SpineActionMessage,
  SpineConfigMessage,
  SpineMessage,
  SpineStatusMessage,
  ValveActionMessage,
  ValveMessage,
  ValveSensor,
  ValveSensorAggregate,
  ValveSensorField,
  ValveSensorUnit,
  ValveStatusMessage
} from '../types'
import i18next from 'i18next'

export function formatValveSensor(value: number | null, field: ValveSensorField, unit = true) {
  switch (field) {
    case 'setTemperature':
    case 'temperature':
    case 'attachedTemperature':
    case 'nearbyTemperature':
    case 'inTemperature':
    case 'outTemperature':
    case 'attachedDewPoint':
    case 'nearbyDewPoint':
    case 'inDewPoint':
    case 'outDewPoint':
      return formatValveSensorTemperature(value, unit)
    case 'humidity':
    case 'attachedHumidity':
    case 'nearbyHumidity':
    case 'inHumidity':
    case 'outHumidity':
    case 'setPoint':
    case 'relativePosition':
      return formatValveSensorHumidity(value, unit)
    case 'absolutePosition':
      return formatValveSensorAbsolutePosition(value, unit)
    case 'attachedPressure':
    case 'nearbyPressure':
    case 'inPressure':
    case 'outPressure':
      return formatValveSensorPressure(value, unit)
    case 'rssi':
      return formatValveSensorRssi(value, unit)
    default:
      return value
  }
}

export function formatValveSensorTemperature(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}°C` : value
}

export function formatValveSensorHumidity(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}%` : value
}

export function formatValveSensorPressure(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}HPa` : value
}

export function formatValveSensorAbsolutePosition(value: number | null, unit = true) {
  if (value === null) {
    return null
  }

  const formattedValue = Math.round(value / 100)

  return unit ? `${formattedValue}°` : `${formattedValue}`
}

export function formatValveSensorPosition(value: number | null, unit = true) {
  const formattedValue = value !== null ? Math.round((value / 100) * 10) / 10 : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}%` : value
}

export function formatValveSensorRssi(value: number | null, unit = true) {
  const formattedValue = value !== null ? parseFloat(value.toFixed(2)) : value

  return !unit ? formattedValue : value !== null ? `${formattedValue}dBm` : value
}

export function makeValveSensorAggregateKey(
  field: ValveSensorField,
  statistic: 'min' | 'max' | 'avg'
): keyof ValveSensorAggregate {
  return `${statistic}${field.charAt(0).toUpperCase()}${field.slice(1)}` as keyof ValveSensorAggregate
}

export function isValveMessage(message: DeviceMessage): message is DeviceMessage {
  return isValveActionMessage(message) || isValveStatusMessage(message)
}

export function isValveActionMessage(message: DeviceMessage): message is ValveActionMessage {
  return message.kind === 'ValveAction'
}

export function isValveStatusMessage(message: DeviceMessage): message is ValveStatusMessage {
  return message.kind === 'ValveStatus'
}

export function isSpineMessage(message: DeviceMessage): message is SpineMessage {
  return (
    isSpineActionMessage(message) || isSpineConfigMessage(message) || isSpineStatusMessage(message)
  )
}

export function isSpineActionMessage(message: DeviceMessage): message is SpineActionMessage {
  return message.kind === 'SpineAction'
}

export function isSpineConfigMessage(message: DeviceMessage): message is SpineConfigMessage {
  return message.kind === 'SpineConfig'
}

export function isSpineStatusMessage(message: DeviceMessage): message is SpineStatusMessage {
  return message.kind === 'SpineStatus'
}

export function formatDeviceMessage(message: DeviceMessage): string {
  if (isSpineMessage(message)) {
    return formatSpineMessage(message)
  } else if (isValveMessage(message)) {
    return formatValveMessage(message)
  } else {
    return i18next.t('unknownDeviceMessageWasSent', { ns: 'valve' })
  }
}

export function formatSpineMessage(message: SpineMessage): string {
  if (isSpineActionMessage(message)) {
    return formatSpineActionMessage(message)
  } else if (isSpineConfigMessage(message)) {
    return formatSpineConfigMessage(message)
  } else if (isSpineStatusMessage(message)) {
    return formatSpineStatusMessage(message)
  } else {
    return i18next.t('unknownSpineMessageWasSent', { ns: 'valve' })
  }
}

export function formatSpineActionMessage(message: SpineActionMessage) {
  if (isMultiAction(message)) {
    return i18next.t('spineActionWasSent', { ns: 'valve' })
  } else if (isRebootAction(message)) {
    return i18next.t('rebootActionWasSent', { ns: 'valve' })
  } else if (isRelayAction(message)) {
    if (message.relay === 1) {
      return i18next.t('relayWasTurnedOn', { ns: 'valve' })
    } else {
      return i18next.t('relayWasTurnedOff', { ns: 'valve' })
    }
  } else if (isDfuAction(message)) {
    return formatDfuMessage(message.dfu!)
  } else if (isEnergyCounterAction(message)) {
    return i18next.t('newEnergyCounterWasSet', {
      ns: 'valve',
      energyCounter: message.energy_counter
    })
  } else if (isClearEnergyCounterAction(message)) {
    return i18next.t('energyCounterWasCleared', { ns: 'valve' })
  } else if (isStatusAction(message)) {
    return i18next.t('spineStatusWasRequested', { ns: 'valve' })
  } else if (isRuuviPairingAction(message)) {
    return i18next.t('ruuviTagPairingWasActivated', { ns: 'valve' })
  } else if (isADCAction(message)) {
    return i18next.t('adcActionWasSent', { ns: 'valve' })
  } else {
    return i18next.t('unknownSpineActionWasSent', { ns: 'valve' })
  }
}

export function formatSpineConfigMessage(_: SpineConfigMessage) {
  return i18next.t('spineConfigWasSent', { ns: 'valve' })
}

export function formatSpineStatusMessage(message: SpineStatusMessage) {
  if (isRebootMessage(message)) {
    return i18next.t('rebootStatusWasSent', { ns: 'valve' })
  } else if (isADCMessage(message)) {
    return i18next.t('adcStatusWasSent', { ns: 'valve' })
  } else {
    return i18next.t('spineStatusWasSent', { ns: 'valve' })
  }
}

export function formatValveMessage(message: ValveMessage): string {
  if (isValveActionMessage(message)) {
    return formatValveActionMessage(message)
  } else if (isValveStatusMessage(message)) {
    return formatValveStatusMessage(message)
  } else {
    return i18next.t('unknownValveMessageWasSent', { ns: 'valve' })
  }
}

export function formatValveActionMessage(message: ValveActionMessage): string {
  if (isTempTargetAction(message)) {
    return i18next.t('newSetTemperatureWasSet', {
      ns: 'valve',
      temperature: message.temp_target
    })
  } else if (isMinPositionAction(message)) {
    return i18next.t('newMinPositionWasSet', {
      ns: 'valve',
      position: Math.round(message.min_position! / 100)
    })
  } else if (isUpdateCycleAction(message)) {
    return i18next.t('newUpdateCycleWasSet', {
      ns: 'valve',
      seconds: message.update_cycle
    })
  } else if (isRuuviPairAction(message)) {
    return i18next.t('newRuuviTagsWerePaired', { ns: 'valve' })
  } else if (isDfuAction(message)) {
    return formatDfuMessage(message.dfu!)
  } else if (isBelimoAction(message)) {
    return i18next.t('belimoActionWasSent', { ns: 'valve' })
  } else if ('get_status' in message) {
    return i18next.t('valveStatusWasRequested', { ns: 'valve' })
  } else {
    return i18next.t('unknownValveActionWasSent', { ns: 'valve' })
  }
}

export function formatValveStatusMessage(_: ValveStatusMessage): string {
  return i18next.t('valveStatusWasSent', { ns: 'valve' })
}

function formatDfuMessage(dfu: number): string {
  switch (dfu) {
    case -25:
      return i18next.t('firmwareUpdateToValveWasRequested', { ns: 'valve' })
    case -26:
      return i18next.t('firmwareUpdateToSpineWasRequested', { ns: 'valve' })
    case 99:
      return i18next.t('firmwareUpdateViaFleetControlWasRequested', { ns: 'valve' })
    default:
      return i18next.t('firmwareUpdateWasRequested', {
        ns: 'valve',
        secondss: dfu
      })
  }
}

function isRebootMessage(message: SpineStatusMessage): boolean {
  return message?.type === 'reboot'
}

function isADCMessage(message: SpineStatusMessage): boolean {
  return (
    message?.type === 'adc_raw_data' || message?.type === 'adc_raw' || message?.type === 'adc_conv'
  )
}

function isMultiAction(message: SpineActionMessage): boolean {
  return (
    [
      isRebootAction(message),
      isRelayAction(message),
      isDfuAction(message),
      isEnergyCounterAction(message),
      isClearEnergyCounterAction(message),
      isStatusAction(message),
      isRuuviPairingAction(message),
      isADCAction(message)
    ].filter((v) => v).length > 1
  )
}

function isRebootAction(message: SpineActionMessage): boolean {
  return 'reboot' in message
}

function isRelayAction(message: SpineActionMessage): boolean {
  return 'relay' in message
}

function isDfuAction(message: SpineActionMessage | ValveActionMessage): boolean {
  return 'dfu' in message
}

function isEnergyCounterAction(message: SpineActionMessage): boolean {
  return 'energy_counter' in message
}

function isClearEnergyCounterAction(message: SpineActionMessage): boolean {
  return 'clear_e_counter' in message
}

function isStatusAction(message: SpineActionMessage): boolean {
  return 'get_status' in message
}

function isRuuviPairingAction(message: SpineActionMessage): boolean {
  return 'ruuvi_pairing' in message
}

function isADCAction(message: SpineActionMessage): boolean {
  return 'adc_data' in message
}

function isRuuviPairAction(message: ValveActionMessage): boolean {
  return 'ruuvi_pair' in message
}

function isUpdateCycleAction(message: ValveActionMessage): boolean {
  return 'update_cycle' in message
}

function isTempTargetAction(message: ValveActionMessage): boolean {
  return 'temp_target' in message
}

function isMinPositionAction(message: ValveActionMessage): boolean {
  return 'min_position' in message
}

function isBelimoAction(message: ValveActionMessage): boolean {
  return 'write' in message
}

export function formatDeviceMessageType(message: DeviceMessage): string {
  if (isValveActionMessage(message)) {
    return i18next.t('action', { ns: 'valve' })
  } else if (isValveStatusMessage(message)) {
    return i18next.t('status', { ns: 'valve' })
  } else if (isSpineActionMessage(message)) {
    return i18next.t('action', { ns: 'valve' })
  } else if (isSpineConfigMessage(message)) {
    return i18next.t('config', { ns: 'valve' })
  } else if (isSpineStatusMessage(message)) {
    return i18next.t('status', { ns: 'valve' })
  } else {
    return i18next.t('unknown', { ns: 'valve' })
  }
}

/**
 * Get a unit for a given field.
 */
export function getValveSensorUnitByField(field: ValveSensorField): ValveSensorUnit | undefined {
  switch (field) {
    case 'setTemperature':
    case 'temperature':
    case 'attachedTemperature':
    case 'nearbyTemperature':
    case 'inTemperature':
    case 'outTemperature':
    case 'attachedDewPoint':
    case 'nearbyDewPoint':
    case 'inDewPoint':
    case 'outDewPoint':
      return '°C'
    case 'humidity':
    case 'attachedHumidity':
    case 'nearbyHumidity':
    case 'inHumidity':
    case 'outHumidity':
    case 'setPoint':
    case 'relativePosition':
      return '%'
    case 'absolutePosition':
      return '°'
    case 'attachedPressure':
    case 'nearbyPressure':
    case 'inPressure':
    case 'outPressure':
      return 'hPa'
    default:
      return undefined
  }
}

export function translateValveSensorField(field: ValveSensorField) {
  switch (field) {
    case 'temperature':
      return i18next.t('deviceTemperature', { ns: 'valve' })
    case 'humidity':
      return i18next.t('deviceHumidity', { ns: 'valve' })
    default:
      return i18next.t(field, { ns: 'valve' })
  }
}

export function isValveSensor(
  valveSensor: ValveSensor | ValveSensorAggregate
): valveSensor is ValveSensor {
  return (valveSensor as ValveSensor).time !== undefined
}

export function isValveSensorAggregate(
  valveSensor: ValveSensor | ValveSensorAggregate
): valveSensor is ValveSensorAggregate {
  return (valveSensor as ValveSensorAggregate).timeBucket !== undefined
}
